import { Check, Grid3X3, MoreVertical } from 'lucide-react';
import type { KeyboardEvent, MouseEvent } from 'react';
import type { SquaresMyPick } from '../../api/getMyGames';
import { navigateToGameUrl } from '../../utils/lobbyMyGamesNavigation';
import type { SquaresCardScores } from '../../utils/lobbyMyGamesSquaresScores';
import { getMyGamesSportLogoPath } from '../../utils/myGamesSportLogo';
import { getReservedStatusLabel } from '../../utils/reservedStatusLabel';

interface LobbySquaresCardProps {
  pick: SquaresMyPick;
  /** Full public URL with token appended (or null if unavailable). */
  publicUrl: string | null;
  /**
   * Live or final scores from `/api/squares/fixture-scores`, already mapped to row/col teams.
   * Null/undefined keeps the dash placeholders until the feed answers.
   */
  scores?: SquaresCardScores | null;
}

function formatPrize(amount: number): string {
  return `$${amount.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
}

export default function LobbySquaresCard({ pick, publicUrl, scores }: LobbySquaresCardProps) {
  const statusUpper = (pick.status ?? '').toUpperCase();
  const isLive = statusUpper === 'ACTIVE' || pick.status === 'live';
  const isCompleted =
    statusUpper === 'COMPLETE' || pick.status === 'final' || pick.status === 'completed';

  const title = pick.contest_name_en || pick.contest_name_fr || 'Squares Contest';
  const prizePool = formatPrize(pick.total_prize_amount ?? 0);
  const entryFee =
    pick.entry_fee === 0 || pick.entry_fee == null ? 'FREE' : formatPrize(pick.entry_fee as number);
  const hasWinnings = (pick.total_winnings ?? 0) > 0;
  const logoPath = getMyGamesSportLogoPath(pick.sport);
  const rowScoreDisplay = scores != null ? String(scores.rowScore) : '—';
  const colScoreDisplay = scores != null ? String(scores.colScore) : '—';

  // Reserved-seat entry with no submitted picks (DKPLAT-533) — same three-state
  // label the Pick'em card shows, so both game types read identically in My Games.
  //
  // Both signals fail CLOSED when the field is absent: a squares "question" is the
  // grid, which exists from contest creation, so an unknown reveal signal can never
  // be taken to mean "open for picks". That fail-open is what rendered
  // "No Picks Submitted - Make Selections Now!" on a contest with no posted-at time.
  const hasSelections = pick.has_selections === true;
  const questionsAvailable = pick.has_questions === true;
  const showReservedLabel = !!pick.has_reservation && !hasSelections;
  const reservedLabel = showReservedLabel
    ? getReservedStatusLabel(
        isCompleted ? 'final' : isLive ? 'live' : 'upcoming',
        questionsAvailable
      )
    : null;

  const resultsUrl = publicUrl
    ? `${publicUrl}${publicUrl.includes('?') ? '&' : '?'}mode=results`
    : null;

  const cardTargetUrl = isCompleted && resultsUrl ? resultsUrl : publicUrl;

  const handleNavigate = (e?: MouseEvent | KeyboardEvent) => {
    if (!cardTargetUrl) return;
    e?.preventDefault();
    navigateToGameUrl(cardTargetUrl);
  };

  return (
    // Entire card is tappable — navigates to play (or results when completed)
    <a
      href={cardTargetUrl ?? undefined}
      role={cardTargetUrl ? 'link' : undefined}
      tabIndex={cardTargetUrl ? 0 : -1}
      aria-disabled={!cardTargetUrl}
      onClick={handleNavigate}
      onKeyDown={e => {
        if (e.key === 'Enter' || e.key === ' ') handleNavigate(e);
      }}
      className={`block bg-[#242424] rounded-xl p-4 transition-all duration-200 ${
        isLive ? 'border-2 border-[#53d337]' : 'border border-[#373737]'
      } ${cardTargetUrl ? 'cursor-pointer hover:ring-2 hover:ring-[#53d337]/30 active:scale-[0.98]' : 'cursor-default pointer-events-none'}`}
    >
      {/* Top Row: Logo + Title + Status */}
      <div className="flex items-start gap-3 mb-4">
        {/* League logo */}
        <div className="w-12 h-12 flex-shrink-0 rounded-lg bg-[#373737] flex items-center justify-center overflow-hidden">
          {logoPath ? (
            <img
              src={logoPath}
              alt={`${pick.sport} logo`}
              className="w-full h-full object-contain p-1"
            />
          ) : (
            <span className="text-zinc-400 font-medium text-[10px]">Logo</span>
          )}
        </div>

        {/* Title + status */}
        <div className="flex-1 min-w-0">
          <h3 className="text-white font-semibold text-sm line-clamp-2">{title}</h3>
          <div className="flex items-center gap-2 mt-0.5">
            {isLive ? (
              <>
                <span className="w-2 h-2 rounded-full bg-[#53d337] animate-pulse" />
                <span className="text-[#53d337] font-semibold text-sm">LIVE</span>
                <span className="text-zinc-500 text-sm font-normal">(1 Game)</span>
              </>
            ) : isCompleted ? (
              <>
                <span className="text-zinc-400 font-semibold text-sm">Final</span>
                <span className="text-zinc-500 text-sm font-normal">(1 Game)</span>
              </>
            ) : (
              <>
                <span className="text-zinc-500 text-sm font-normal">Start:</span>
                <span className="text-[#53d337] font-semibold text-sm">
                  {pick.start_time_utc
                    ? new Date(pick.start_time_utc).toLocaleString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        hour: 'numeric',
                        minute: '2-digit',
                      })
                    : 'TBD'}
                </span>
                <span className="text-zinc-500 text-sm font-normal">(1 Game)</span>
              </>
            )}
          </div>
        </div>

        <MoreVertical className="w-5 h-5 text-zinc-400 flex-shrink-0 mt-0.5" />
      </div>

      {/* Scores row (live / completed) or stats row (upcoming) */}
      {(isLive || isCompleted) && pick.row_team_display_name && pick.col_team_display_name ? (
        <div className="flex items-center mb-4">
          <div className="flex-1">
            <p className="text-zinc-500 text-xs mb-0.5">{pick.row_team_display_name}</p>
            <p
              data-testid={`my-games-squares-row-score-${pick.squares_contest_id}`}
              className="text-white font-semibold text-lg"
            >
              {rowScoreDisplay}
            </p>
          </div>
          <div className="w-px h-8 bg-[#373737] mx-4" />
          <div className="flex-1">
            <p className="text-zinc-500 text-xs mb-0.5">{pick.col_team_display_name}</p>
            <p
              data-testid={`my-games-squares-col-score-${pick.squares_contest_id}`}
              className="text-white font-semibold text-lg"
            >
              {colScoreDisplay}
            </p>
          </div>
          <div className="w-px h-8 bg-[#373737] mx-4" />
          <div className="flex-1 text-right">
            <p className="text-zinc-500 text-xs mb-0.5">Total Winnings</p>
            <p
              className={`font-semibold text-lg ${hasWinnings ? 'text-[#53d337]' : 'text-zinc-500'}`}
            >
              {formatPrize(pick.total_winnings ?? 0)}
            </p>
          </div>
        </div>
      ) : (
        <div className="flex items-center mb-4">
          <div className="flex-1">
            <p className="text-zinc-500 text-xs mb-0.5">Entries</p>
            <p className="text-white font-semibold">{pick.total_entries_to_date ?? 0}</p>
          </div>
          <div className="w-px h-8 bg-[#373737] mx-4" />
          <div className="flex-1">
            <p className="text-zinc-500 text-xs mb-0.5">Entry</p>
            <p className="text-white font-semibold">{entryFee}</p>
          </div>
          <div className="w-px h-8 bg-[#373737] mx-4" />
          <div className="flex-1 text-right">
            <p className="text-zinc-500 text-xs mb-0.5">Prize Pool</p>
            <p className="text-[#53d337] font-semibold">{prizePool}</p>
          </div>
        </div>
      )}

      {/* Reserved-seat status label (DKPLAT-533) — persistent, no picks submitted.
          State 2 ("Make Selections Now!") is emphasised as the user needs to act.
          Markup mirrors LobbyPickemCard so the two cards are visually identical. */}
      {reservedLabel && (
        <div className="mb-4 flex justify-center">
          <span
            className={`inline-flex items-center justify-center rounded-full px-4 py-1.5 text-center text-[13px] font-semibold ${
              reservedLabel.emphasised
                ? 'bg-[#53d337] text-black shadow-[0_0_12px_rgba(83,211,55,0.45)]'
                : 'border border-[#373737] bg-[#121212] text-[#ABABAB]'
            }`}
          >
            {reservedLabel.text}
          </span>
        </div>
      )}

      {/* Action buttons — <button> (not <a>) to avoid nested anchor tags */}
      {(isLive || isCompleted) && (
        <div className="flex justify-center gap-2 flex-wrap">
          {resultsUrl && (
            <button
              type="button"
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                window.location.href = resultsUrl;
              }}
              className="w-[132px] h-[32px] rounded-full border border-[#373737] bg-[#121212] text-white flex items-center justify-center gap-2 transition-colors hover:border-[#53d337]"
            >
              <span className="text-[13px] font-semibold">View Results</span>
              <span className="w-[13px] h-[13px] rounded-full bg-[#6F6F6F] flex items-center justify-center">
                <Check className="w-[7px] h-[7px] text-black" strokeWidth={3} />
              </span>
            </button>
          )}
          {publicUrl && (
            <button
              type="button"
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                window.location.href = publicUrl;
              }}
              className="w-[132px] h-[32px] rounded-full border border-[#373737] bg-[#121212] text-white flex items-center justify-center gap-2 transition-colors hover:border-[#53d337]"
            >
              <span className="text-[13px] font-semibold">View Game</span>
              <span className="w-[13px] h-[13px] rounded-full bg-[#6F6F6F] flex items-center justify-center">
                <Grid3X3 className="w-[8px] h-[8px] text-black" strokeWidth={3} />
              </span>
            </button>
          )}
        </div>
      )}
    </a>
  );
}
